import { Button, Flex, Modal } from 'antd';
import { LuPlay } from 'react-icons/lu';

import { DETECTION_OUTCOME } from '@utils/detectionOutcome';

import DetectionSummary from './DetectionSummary';

/**
 * Confirmation dialog for starting a detection run, for flows that don't have a
 * wizard step to host the review (the map's boundary editor, today).
 *
 * Wraps the shared {@link DetectionSummary} so the review reads the same here as
 * in the Object Picker's Run step. Every summary prop is passed straight
 * through; the modal only owns the shell and the two actions.
 *
 * Unlike the Object Picker, this dialog keeps antd's default mask. The picker
 * can afford `mask={false}` because its own state is what's under review; here
 * the map behind the dialog is live, and the boundary editor reads its target
 * collection at confirm time — so leaving the map clickable would let the user
 * switch collections and have the confirmed run land on the new one. Do not set
 * `mask={false}` here.
 *
 * `confirming` gates every dismissal path, not just the visible ones: declining
 * cannot undo a run the server has already started, so while a confirmed request
 * is in flight the close icon (`closable`), ESC (`keyboard`), the mask click
 * (`mask.closable`) and the footer Cancel are all inert. `closable` on its own
 * covers only the icon.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onConfirm       - start the run
 * @param {() => void} props.onCancel        - decline (also fired by ESC, the close
 *   icon and the mask click, all suppressed while `confirming`)
 * @param {boolean} [props.confirming=false] - a confirmed request is in flight
 * @param {string} [props.title='Run detection?']
 * @param {string} [props.confirmText='Run detection']
 * @param {string} [props.cancelText='Cancel']
 * @param {string} [props.collectionName]
 * @param {Array<{key: string, label: string, color?: string}>} [props.objects]
 * @param {string} [props.areaLabel]
 * @param {import('react').ReactNode} [props.areaIcon]
 * @param {number|null} [props.scanAreaSquareMeters]
 * @param {string} [props.heading]
 * @param {string} [props.description]
 */
const ConfirmDetectionModal = ({
	open,
	onConfirm,
	onCancel,
	confirming = false,
	title = 'Run detection?',
	confirmText = 'Run detection',
	cancelText = 'Cancel',
	collectionName,
	objects,
	areaLabel,
	areaIcon,
	scanAreaSquareMeters = null,
	heading,
	description,
}) => (
	<Modal
		centered
		open={open}
		title={title}
		onCancel={onCancel}
		closable={!confirming}
		keyboard={!confirming}
		mask={{ enabled: true, closable: !confirming }}
		width={{ xs: '90%', sm: '80%', md: 560, lg: 560, xl: 560, xxl: 560 }}
		footer={
			<Flex justify="space-between" align="center">
				<Button onClick={onCancel} disabled={confirming}>
					{cancelText}
				</Button>
				<Button type="primary" icon={<LuPlay />} loading={confirming} onClick={onConfirm}>
					{confirmText}
				</Button>
			</Flex>
		}>
		<DetectionSummary
			heading={heading}
			description={description}
			collectionName={collectionName}
			objects={objects}
			areaLabel={areaLabel}
			areaIcon={areaIcon}
			scanAreaSquareMeters={scanAreaSquareMeters}
			outcome={DETECTION_OUTCOME.NEEDS_CONFIRMATION}
		/>
	</Modal>
);

export default ConfirmDetectionModal;
